import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Search, Network, Server, Trash2, MoreHorizontal } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Label } from '@/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { apiClient } from '@/lib/api-client'
import { useToast } from '@/hooks/use-toast'

interface Subnet {
  id: string
  network: string
  gateway: string | null
  vlanId: number | null
  vlanName: string | null
  description: string | null
  site: string | null
  isActive: boolean
  tags: string[]
  _count?: { addresses: number }
}

function AddSubnetDialog() {
  const [open, setOpen] = useState(false)
  const [network, setNetwork] = useState('')
  const [gateway, setGateway] = useState('')
  const [vlanId, setVlanId] = useState('')
  const [vlanName, setVlanName] = useState('')
  const [description, setDescription] = useState('')
  const [site, setSite] = useState('')
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const createMutation = useMutation({
    mutationFn: (data: Record<string, unknown>) => apiClient.post('/ipam/subnets', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipam-subnets'] })
      toast({ title: 'Subnet added', description: 'Subnet created successfully' })
      setOpen(false)
      setNetwork('')
      setGateway('')
      setVlanId('')
      setVlanName('')
      setDescription('')
      setSite('')
    },
    onError: () => {
      toast({ title: 'Error', description: 'Failed to create subnet', variant: 'destructive' })
    },
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button><Plus className="mr-2 h-4 w-4" /> Add Subnet</Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Add Subnet</DialogTitle>
          <DialogDescription>Add a new IP subnet to track.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>Network (CIDR)</Label>
            <Input value={network} onChange={(e) => setNetwork(e.target.value)} placeholder="192.168.1.0/24" />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Gateway</Label>
              <Input value={gateway} onChange={(e) => setGateway(e.target.value)} placeholder="192.168.1.1" />
            </div>
            <div className="space-y-2">
              <Label>Site</Label>
              <Input value={site} onChange={(e) => setSite(e.target.value)} placeholder="Sydney HQ" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>VLAN ID</Label>
              <Input type="number" value={vlanId} onChange={(e) => setVlanId(e.target.value)} placeholder="100" />
            </div>
            <div className="space-y-2">
              <Label>VLAN Name</Label>
              <Input value={vlanName} onChange={(e) => setVlanName(e.target.value)} placeholder="Management" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Description</Label>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Management network" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({
              network,
              ...(gateway && { gateway }),
              ...(vlanId && { vlanId: parseInt(vlanId) }),
              ...(vlanName && { vlanName }),
              ...(description && { description }),
              ...(site && { site }),
            })}
            disabled={!network || createMutation.isPending}
          >
            {createMutation.isPending ? 'Adding...' : 'Add Subnet'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function SubnetCard({ subnet }: { subnet: Subnet }) {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  const deleteMutation = useMutation({
    mutationFn: () => apiClient.delete(`/ipam/subnets/${subnet.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['ipam-subnets'] })
      toast({ title: 'Deleted', description: 'Subnet deleted' })
    },
  })

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <Network className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-base font-mono">{subnet.network}</CardTitle>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon"><MoreHorizontal className="h-4 w-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem className="text-destructive" onClick={() => deleteMutation.mutate()}>
              <Trash2 className="mr-2 h-4 w-4" /> Delete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </CardHeader>
      <CardContent>
        <div className="flex flex-wrap gap-2 mb-3">
          {subnet.vlanId && (
            <Badge variant="outline">VLAN {subnet.vlanId}{subnet.vlanName ? ` - ${subnet.vlanName}` : ''}</Badge>
          )}
          {subnet.site && <Badge variant="secondary">{subnet.site}</Badge>}
          {subnet._count && (
            <Badge variant="outline" className="flex items-center gap-1">
              <Server className="h-3 w-3" /> {subnet._count.addresses} addresses
            </Badge>
          )}
        </div>
        <div className="text-sm text-muted-foreground space-y-1">
          {subnet.gateway && <p>Gateway: {subnet.gateway}</p>}
          {subnet.description && <p>{subnet.description}</p>}
        </div>
      </CardContent>
    </Card>
  )
}

export function IpamPage() {
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['ipam-subnets'],
    queryFn: () => apiClient.get<{ subnets: Subnet[]; pagination: unknown }>('/ipam/subnets'),
  })

  const subnets = data?.data?.subnets || []
  const filtered = subnets.filter((s) =>
    s.network.includes(search) ||
    s.description?.toLowerCase().includes(search.toLowerCase()) ||
    s.site?.toLowerCase().includes(search.toLowerCase())
  )

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">IPAM</h1>
          <p className="text-muted-foreground">IP Address Management - subnets, VLANs, and addresses</p>
        </div>
        <AddSubnetDialog />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search subnets..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[...Array(6)].map((_, i) => (
            <Card key={i}><CardHeader><Skeleton className="h-5 w-32" /></CardHeader><CardContent><Skeleton className="h-4 w-full" /></CardContent></Card>
          ))}
        </div>
      ) : filtered.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Network className="h-12 w-12 mb-4 opacity-50 text-muted-foreground" />
            <p className="text-lg font-medium text-muted-foreground">No subnets found</p>
            <p className="text-sm text-muted-foreground">Add a subnet to start managing your IP space</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {filtered.map((subnet) => (
            <SubnetCard key={subnet.id} subnet={subnet} />
          ))}
        </div>
      )}
    </div>
  )
}
